import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { cx } from "../cx";
import styles from "./DatePicker.module.css";
import {
  addDays,
  addMonths,
  type DateRange,
  MONTH_NAMES,
  monthGrid,
  sameDay,
  startOfDay,
  WEEKDAY_LABELS_MONDAY,
  WEEKDAY_LABELS_SUNDAY,
  type WeekStart,
} from "./dates";

export interface MonthCalendarProps {
  month: Date;
  weekStart: WeekStart;
  today: Date;
  selected?: DateRange | null;
  /** Live preview while picking the second end of a range. */
  preview?: DateRange | null;
  onPick: (day: Date) => void;
  onHover?: (day: Date | null) => void;
  /** Adds prev/next arrows to the month title. */
  onPrev?: () => void;
  onNext?: () => void;
}

function within(d: Date, r: DateRange | null | undefined): boolean {
  if (!r) return false;
  const t = startOfDay(d).getTime();
  return t >= r.start.getTime() && t <= r.end.getTime();
}

/** The day a key moves to from `d` (WAI-ARIA date picker grid pattern), or null for keys the grid ignores. */
function keyTarget(e: KeyboardEvent, d: Date, weekStart: WeekStart): Date | null {
  const col = (d.getDay() - (weekStart === "MONDAY" ? 1 : 0) + 7) % 7;
  switch (e.key) {
    case "ArrowLeft":
      return addDays(d, -1);
    case "ArrowRight":
      return addDays(d, 1);
    case "ArrowUp":
      return addDays(d, -7);
    case "ArrowDown":
      return addDays(d, 7);
    case "Home":
      return addDays(d, -col);
    case "End":
      return addDays(d, 6 - col);
    case "PageUp":
      return addMonths(d, e.shiftKey ? -12 : -1);
    case "PageDown":
      return addMonths(d, e.shiftKey ? 12 : 1);
    default:
      return null;
  }
}

export function MonthCalendar({
  month,
  weekStart,
  today,
  selected,
  preview,
  onPick,
  onHover,
  onPrev,
  onNext,
}: MonthCalendarProps) {
  const labels = weekStart === "MONDAY" ? WEEKDAY_LABELS_MONDAY : WEEKDAY_LABELS_SUNDAY;
  const range = preview ?? selected;
  const days = monthGrid(month, weekStart);
  const titleId = useId();
  const gridRef = useRef<HTMLTableElement>(null);
  // One tab stop per month (roving tabindex); arrows move it, and moving past the grid turns the month.
  const [focusDay, setFocusDay] = useState<Date | null>(null);
  const moved = useRef(false);
  const inMonth = (d: Date | null | undefined): d is Date =>
    !!d && d.getMonth() === month.getMonth() && d.getFullYear() === month.getFullYear();
  const tabStop =
    (focusDay && days.some((x) => sameDay(x, focusDay)) ? focusDay : null) ??
    [range?.start, today].find(inMonth) ??
    new Date(month.getFullYear(), month.getMonth(), 1);

  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    gridRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
  });

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, d: Date) => {
    const raw = keyTarget(e, d, weekStart);
    if (raw === null) return;
    e.preventDefault();
    const target = startOfDay(raw);
    const first = days[0] as Date;
    const last = days[days.length - 1] as Date;
    // PageUp/PageDown always change month; arrows stay on the grid, which already shows adjacent-month days.
    const leavesMonth = target.getMonth() !== month.getMonth() || target.getFullYear() !== month.getFullYear();
    const page = e.key === "PageUp" || e.key === "PageDown";
    if (page || target < first || target > last) {
      if (!leavesMonth) return;
      const back = target < month;
      const turn = back ? onPrev : onNext;
      if (!turn) return;
      // A year jump turns twelve times, so each step stays a plain onPrev/onNext for the parent.
      const steps = page && e.shiftKey ? 12 : 1;
      for (let i = 0; i < steps; i++) turn();
    }
    setFocusDay(target);
    moved.current = true;
  };

  const weeks = Array.from({ length: days.length / 7 }, (_, w) => days.slice(w * 7, w * 7 + 7));
  return (
    <div className={styles.month}>
      <div className={styles.monthTitle}>
        {onPrev ? (
          <button type="button" className={styles.arrow} aria-label="Previous month" onClick={onPrev}>
            ‹
          </button>
        ) : (
          <span className={styles.arrowSpacer} />
        )}
        <span id={titleId} aria-live="polite">
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </span>
        {onNext ? (
          <button type="button" className={styles.arrow} aria-label="Next month" onClick={onNext}>
            ›
          </button>
        ) : (
          <span className={styles.arrowSpacer} />
        )}
      </div>
      <table ref={gridRef} aria-labelledby={titleId} className={styles.grid}>
        <thead className={styles.row}>
          <tr className={styles.row}>
            {labels.map((l) => (
              <th key={l} scope="col" className={styles.weekday}>
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.row}>
          {weeks.map((week) => (
            <tr key={week[0]?.toISOString()} className={styles.row}>
              {week.map((d) => {
                const outside = d.getMonth() !== month.getMonth();
                const inRange = within(d, range);
                const isEdge = !!range && (sameDay(d, range.start) || sameDay(d, range.end));
                return (
                  <td key={d.toISOString()} className={styles.row}>
                    <button
                      type="button"
                      tabIndex={sameDay(d, tabStop) ? 0 : -1}
                      aria-pressed={inRange}
                      aria-label={d.toDateString()}
                      className={cx(
                        styles.day,
                        outside && styles.outside,
                        inRange && styles.inRange,
                        isEdge && styles.edge,
                        sameDay(d, today) && styles.today,
                      )}
                      onClick={() => onPick(startOfDay(d))}
                      onKeyDown={(e) => onKeyDown(e, d)}
                      onFocus={() => setFocusDay(startOfDay(d))}
                      onPointerEnter={onHover ? () => onHover(startOfDay(d)) : undefined}
                    >
                      {d.getDate()}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
